import * as React from 'react';
import { useMemo } from 'react';
import { PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Spinner } from '../../';
import { Heading } from '@restworld/ui-ds-base';

import { cn } from '@restworld/utils-common';
interface IUploadCardProps {
  uploadType: UploadType;
  onChange?: (x: React.ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
  testId?: string;
}
export const Upload = React.forwardRef(({ uploadType, loading, onChange, testId, ...rest }: IUploadCardProps, ref) => {
  const mappedItem = useMemo(() => uploadObject[uploadType], [uploadType]);
  const uploadExtensionType = useMemo(() => mappedItem['extension'], [mappedItem]);
  const uploadText = useMemo(() => mappedItem['text'], [mappedItem]);
  const uploadIcon = useMemo(() => mappedItem['icon'], [mappedItem]);
  const acceptedFormats = useMemo(
    () =>
      typeof uploadExtensionType === 'string'
        ? uploadExtensionType
        : Array.isArray(uploadExtensionType)
        ? uploadExtensionType?.join(',')
        : AcceptedFileType.Pdf,
    [uploadExtensionType]
  );
  const { t } = useTranslation('common');
  return (
    <Wrapper>
      <label
        htmlFor={uploadType}
        className="flex flex-col gap-4 items-center justify-center w-full max-w-[20rem] h-[17rem] border-2 border-primary-500 border-dashed rounded-[16px] cursor-pointer bg-primary-100"
        {...rest}
      >
        <div className="flex flex-col items-center justify-center gap-1">
          {uploadIcon}
          <p className="mb-2 text-sm text-black">
            <span className="font-semibold">{t('common:load')}</span>{' '}
            {uploadText === 'video' ? t('common:one_of_yours') : t('common:your_own')} {uploadText}
          </p>
          <Heading as="span" size={6} weight="semibold" className="text-xs">
            Max. File Size: 30MB
          </Heading>
        </div>
        <StyledUploadButton htmlFor={uploadType} loading={loading} data-cy={testId}>
          {loading ? <Spinner size="sm" color="gray" /> : `${t('common:load')} ${uploadText}`}
        </StyledUploadButton>
        <Input onChange={onChange} type="file" id={uploadType} accept={acceptedFormats} hidden />
      </label>
    </Wrapper>
  );
});
export default Upload;
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full flex items-center justify-center', className)} {...props} />
);
const Input = ({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input className={cn('', className)} {...props} />
);
const StyledUploadButton = ({ loading, className, ...props }: any) => (
  <label
    className={cn(
      'inline-flex items-center max-w-md cursor-pointer',
      'bg-primary-800 focus:ring-4 focus:ring-primary-300 focus:outline-none',
      'px-4 py-3 rounded-lg',
      'text-xs font-medium text-white',
      loading ? 'pointer-events-none' : '',
      className
    )}
    {...props}
  />
);
// --> const
const AcceptedFileType = {
  Text: '.txt',
  Gif: '.gif',
  Jpeg: '.jpg',
  Png: '.png',
  Doc: '.doc',
  Pdf: '.pdf',
  AllImages: 'image/*',
  AllVideos: 'video/*',
  AllAudios: 'audio/*',
};
export const uploadObject = {
  picture: {
    icon: <PhotoIcon className="h-8 w-8 text-primary-500" />,
    text: 'foto',
    extension: ['.jpg', '.jpeg', '.png'],
  },
  video: {
    icon: <VideoCameraIcon className="h-8 w-8 text-primary-500" />,
    text: 'video',
    extension: ['.mp4', '.mp4v', '.mov', '.ogv', '.webm'],
  },
  // file: {
  //   icon: <img src={getAssetSrc('/utils-assets/cloud-upload.svg')} alt="" />,
  //   text: 'file',
  //   extension: ['.pdf', '.doc', '.docx'],
  // },
};
export type UploadType = keyof typeof uploadObject;
